import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Star, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewDialog = ({ open, sellerId, sellerName, listingId, onClose, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [canReview, setCanReview] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(true);

  useEffect(() => {
    if (open && sellerId) {
      checkReviewEligibility();
    }
  }, [open, sellerId]);

  const checkReviewEligibility = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCanReview({ can_review: false, reason: 'Please login to leave a review' });
      setCheckingEligibility(false);
      return;
    }

    try {
      setCheckingEligibility(true);
      const response = await fetch(`${API_URL}/api/reviews/can-review/${sellerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setCanReview(data);
    } catch (err) {
      setCanReview({ can_review: false, reason: 'Failed to check eligibility' });
    } finally {
      setCheckingEligibility(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }
    if (title.trim().length < 3) {
      setError('Title must be at least 3 characters');
      return;
    }
    if (content.trim().length < 10) {
      setError('Review must be at least 10 characters');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please login to submit a review');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/reviews/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          seller_id: sellerId,
          rating,
          title: title.trim(),
          content: content.trim(),
          listing_id: listingId || null
        })
      });

      const data = await response.json();

      if (data.success) {
        if (onSubmit) onSubmit(data.review);
        onClose();
        // Reset form
        setRating(0);
        setTitle('');
        setContent('');
      } else {
        setError(data.error || 'Failed to submit review');
      }
    } catch (err) {
      setError('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setTitle('');
    setContent('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Review {sellerName || 'Seller'}</DialogTitle>
          <DialogDescription>
            Share your experience to help the community
          </DialogDescription>
        </DialogHeader>

        {checkingEligibility ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Checking eligibility...</span>
          </div>
        ) : canReview && !canReview.can_review ? (
          <Alert variant="destructive" className="my-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{canReview.reason}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Verified Contact Badge */}
            {canReview?.verified_contact && (
              <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                <CheckCircle className="h-4 w-4" />
                <span>Verified Contact - Your review will be marked as verified</span>
              </div>
            )}

            {/* Star Rating */}
            <div className="space-y-2">
              <Label>Rating</Label>
              <div className="flex items-center space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="focus:outline-none transition-transform hover:scale-110"
                  >
                    <Star
                      className={`h-8 w-8 transition-colors ${
                        star <= (hoverRating || rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
                {rating > 0 && (
                  <span className="ml-3 font-semibold text-lg">{rating}.0</span>
                )}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Review Title</Label>
              <Input
                id="title"
                placeholder="Summarize your experience"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={100}
              />
              <p className="text-xs text-gray-500">{title.length}/100</p>
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label htmlFor="content">Your Review</Label>
              <Textarea
                id="content"
                placeholder="Share your detailed experience with this seller..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={5}
                maxLength={1000}
                className="resize-none"
              />
              <p className="text-xs text-gray-500">
                {content.length}/1000 characters (minimum 10)
              </p>
            </div>

            {/* Submit Button */}
            <div className="flex space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || rating === 0 || title.length < 3 || content.length < 10}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Review'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewDialog;
